import { A } from '@ember/array';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed, setProperties } from '@ember/object';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { sort } from '@ember/object/computed';
import { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import { isBlank, isPresent } from '@ember/utils';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { includes } from '@nullvoxpopuli/ember-composable-helpers';
import { all, dropTask } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import { eq, gt, gte } from 'ember-truth-helpers';
import lt from 'ember-truth-helpers/helpers/lt';
import LocationsTableBulkModalAssignment from 'garaje/components/location-overview/locations/locations-table/bulk-modal-assignment';
import LocationsTableGroupSelection from 'garaje/components/location-overview/locations/locations-table/group-selection';
import type { SortDirection } from 'garaje/components/sortable-header-column';
import SortableHeaderColumn from 'garaje/components/sortable-header-column';
import UiInput from 'garaje/components/ui-input';
import type Group from 'garaje/models/group';
import type SkinnyLocation from 'garaje/models/skinny-location';
import type ContextSwitcherService from 'garaje/services/context-switcher';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LocalStorageService from 'garaje/services/local-storage';
import type StateService from 'garaje/services/state';
import type WindowLocationService from 'garaje/services/window-location';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface LocationsTableWithGroupsSignature {
  Args: {
    /**
     * List of available groups to be assigned
     */
    groups: Group[];
    /**
     * List of skinny-locations
     */
    locations: SkinnyLocation[];
  };
  Element: HTMLDivElement;
}

/**
 * List of locations with group assignment capabilities
 */
export default class LocationsTableWithGroups extends Component<LocationsTableWithGroupsSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare store: StoreService;
  @service declare localStorage: LocalStorageService;
  @service declare windowLocation: WindowLocationService;
  @service declare contextSwitcher: ContextSwitcherService;
  @service declare state: StateService;

  @tracked sortField = 'nameWithCompanyName';
  @tracked sortDirection: SortDirection = 'asc';
  @tracked selectedLocations: SkinnyLocation[] = [];
  @tracked showBulkModalAssignment = false;
  @tracked searchFilter = '';
  /**
   * Groups sorted by `name` to show in the dropdown selector
   */
  sortProperties = ['name'];
  @sort('args.groups', 'sortProperties') sortedGroups!: Group[];

  /**
   * This CP filters the passed `@locations` using the `this.searchFilter`
   *
   * @return {Array<Location>}
   */
  @computed('args.locations.@each.nameWithCompanyName', 'searchFilter')
  get filteredLocations(): SkinnyLocation[] {
    if (isBlank(this.searchFilter)) {
      return this.args.locations;
    }
    const lowerSearchFilter = this.searchFilter.toLowerCase();
    return this.args.locations.filter((location) =>
      location.nameWithCompanyName.toLowerCase().includes(lowerSearchFilter),
    );
  }

  @computed('filteredLocations.@each.{nameWithCompanyName,groups}', 'sortDirection', 'sortField')
  get sortedLocations(): SkinnyLocation[] {
    const sorted = A(this.filteredLocations).sortBy(this.sortField);
    if (this.sortDirection === 'desc') {
      sorted.reverse();
    }
    return sorted;
  }

  get allCheckState(): string {
    if (this.selectedLocations.length === this.args.locations.length) {
      return 'checked';
    } else if (this.selectedLocations.length > 0) {
      return 'indeterminate';
    }
    return '';
  }

  /**
   * This sets a `min-height` value to the locations table so when filtering
   * it doesn't jump and keep a minimum height
   */
  get minHeight(): number {
    const perRow = 63;
    return this.args.locations.length * perRow;
  }
  @computed('selectedLocations.@each.group')
  get anyRemovableLocation(): boolean {
    return this.selectedLocations.some((location) => isPresent(location.group));
  }

  get workplaceSubscription(): boolean {
    return (
      !!this.state.features?.canAccessWorkplaceApplication || !!this.state.features?.canAccessProtectLegacyFeatures
    );
  }

  removeFromGroupTask = dropTask(async () => {
    const locationsWithGroup = this.selectedLocations.filter((location) => isPresent(location.group));
    if (locationsWithGroup.length === 0) {
      return;
    }
    try {
      await all(
        locationsWithGroup.map((location) => {
          const group = location.group;
          A(group?.locations.content).removeObject(location);
          return group?.save();
        }),
      );

      // If the real location is already loaded, reload it.
      await this._reloadPresentRealLocationsTask.perform(locationsWithGroup);

      const message = `${pluralize(locationsWithGroup.length, 'location')} removed from groups.`;
      this.flashMessages.showAndHideFlash('success', message);
      this.selectedLocations = [];
    } catch (e) {
      const errorText = parseErrorForDisplay(e);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  assignGroupTask = dropTask(
    waitFor(async (selectedGroup: Group) => {
      const locationsWithGroup = this.selectedLocations.filter((location) => isPresent(location.group));
      try {
        // Remove any existing group associations
        await all(
          locationsWithGroup.map((location) => {
            const group = location.group;
            A(group?.locations.content).removeObject(location);
            return group?.save();
          }),
        );

        this.selectedLocations.map((location) => A(selectedGroup.locations.content).addObject(location));
        await selectedGroup.save();

        // If the real location is already loaded, reload it.
        await this._reloadPresentRealLocationsTask.perform(this.selectedLocations);

        this.flashMessages.showAndHideFlash('success', 'Saved');
        this.showBulkModalAssignment = false;
        this.selectedLocations = [];
      } catch (e) {
        const errorText = parseErrorForDisplay(e);
        this.flashMessages.showAndHideFlash('error', errorText);
      }
    }),
  );

  @action
  sortByGroups(sortField: string, sortDirection: SortDirection): void {
    setProperties(this, { sortField, sortDirection });
  }

  @action
  switchLocation(location: SkinnyLocation, e: Event): void {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    this.contextSwitcher.locationId = location.id;
    this.windowLocation.assign('/');
  }

  @action
  onLocationSelected(location: SkinnyLocation): void {
    if (this.selectedLocations.includes(location)) {
      this.selectedLocations = this.selectedLocations.filter((selectedLocation) => selectedLocation !== location);
    } else {
      this.selectedLocations = [...this.selectedLocations, location];
    }
  }

  @action
  toggleAllSelected(e: Event): void {
    const target = <HTMLInputElement>e.target;
    if (this.selectedLocations.length === 0) {
      this.selectedLocations = [...this.args.locations];
    } else {
      this.selectedLocations = [];
    }
    target.checked = this.selectedLocations.length > 0;
    target.indeterminate = this.allCheckState === 'indeterminate';
  }

  /**
   * When a group is assigned or removed, if the real location (not the `skinnyLocation`) is already in the store
   * we need to reload it so the association matches.
   */
  _reloadPresentRealLocationsTask = dropTask(async (locations: SkinnyLocation[]) => {
    for (const location of locations) {
      const realLocation = this.store.peekRecord('location', location.id);
      if (realLocation) {
        await realLocation.reload();
      }
    }
  });

  <template>
    <div ...attributes>

      {{#if (gte @locations.length 9)}}
        <div class="mt-8 mb-3 w-1/3">
          <label for="groups-locations-search" class="sr-only">Locations filter</label>
          <UiInput
            data-test-groups-location-search
            @id="groups-locations-filter"
            class="pageSearch border p-2 icon-search max-w-full"
            @type="search"
            @placeholder="Search locations"
            @value={{this.searchFilter}}
            @update={{fn (mut this.searchFilter)}}
          />
        </div>
      {{/if}}
      <table data-header-table aria-hidden="true" class="mt-6">
        <thead>
          <tr class="text-xs" data-test-location-overview-cols>
            {{#if (gt @groups.length 0)}}
              <th class="p-0 pb-2" style="width: 22px;">
                {{#if this.removeFromGroupTask.isRunning}}
                  <img
                    data-test-loading
                    alt="loading"
                    src="/assets/images/loading-grey.gif"
                    style="width: 12px; margin-left: 6px;"
                  />
                {{else}}
                  <input
                    data-test-groups-locations-all-selector
                    type="checkbox"
                    {{! @glint-expect-error - Not an attribute that can be set directly. Check if this works }}
                    indeterminate={{eq this.allCheckState "indeterminate"}}
                    checked={{eq this.allCheckState "checked"}}
                    {{on "click" this.toggleAllSelected}}
                  />
                {{/if}}
              </th>
            {{/if}}
            {{#if (gt this.selectedLocations.length 0)}}
              <th data-test-total-locations-selected class="py-2 w-1/3">
                <span class="text-carbon-50 text-sm">{{pluralize this.selectedLocations.length "location"}}
                  selected</span>
              </th>
              <th data-test-actions-column class="px-0 text-right" style="height: 26.1px;">
                <button
                  data-test-bulk-assign-group
                  {{on "click" (fn (mut this.showBulkModalAssignment) true)}}
                  disabled={{this.removeFromGroupTask.isRunning}}
                  class="btn btn--link btn--small !text-gem-50 mb-1"
                  type="button"
                >
                  Assign to group
                </button>
                {{#if this.anyRemovableLocation}}
                  <button
                    data-test-bulk-remove-group
                    {{on "click" this.removeFromGroupTask.perform}}
                    class="btn btn--link btn--small !text-gem-50 mb-1 ml-4"
                    type="button"
                    disabled={{this.removeFromGroupTask.isRunning}}
                  >
                    {{if this.removeFromGroupTask.isRunning "Removing..." "Remove from group"}}
                  </button>
                {{/if}}
              </th>
            {{else}}
              <th class="py-2 w-1/2">
                <SortableHeaderColumn
                  @name="Location name"
                  @isActive={{eq this.sortField "name"}}
                  @sortDirection={{this.sortDirection}}
                  @sortField="name"
                  @sortAction={{this.sortByGroups}}
                />
              </th>
              {{#if (gt @groups.length 0)}}
                <th data-test-groups-column class="py-2 w-1/2">
                  <div style="width: 200px">
                    <SortableHeaderColumn
                      @name="Group"
                      @isActive={{eq this.sortField "group.name"}}
                      @sortDirection={{this.sortDirection}}
                      @sortField="group.name"
                      @sortAction={{this.sortByGroups}}
                    />
                  </div>
                </th>
              {{/if}}

            {{/if}}
          </tr>
        </thead>
      </table>
      {{! template-lint-disable style-concatenation }}
      <table data-display-table style="height: {{this.minHeight}}px;">
        {{! template-lint-enable style-concatenation }}
        {{! hidden here for table a11y. Done above for floating header }}
        <thead class="sr-only">
          <tr data-test-location-overview-cols>
            {{#if (gt @groups.length 0)}}
              <th scope="col">
                All selector
              </th>
            {{/if}}
            <th scope="col">
              Location names
            </th>
            {{#if (gt @groups.length 0)}}
              <th scope="col">
                Group
              </th>
            {{/if}}
          </tr>
        </thead>
        <tbody>
          {{#each this.sortedLocations as |location|}}
            <tr class="w-full table" data-test-group-location-row={{location.id}}>
              {{#if (gt @groups.length 0)}}
                <td class="p-0 align-middle border-t pt-0.5" style="width: 22px;">
                  <input
                    data-test-group-selection-checkbox
                    disabled={{this.removeFromGroupTask.isRunning}}
                    type="checkbox"
                    checked={{includes location this.selectedLocations}}
                    {{on "click" (fn this.onLocationSelected location)}}
                  />
                </td>
              {{/if}}
              <td class="p-0 w-1/2 border-t align-middle">
                <div class="pl-4 {{if (lt @groups.length 1) 'py-4'}}">
                  <a
                    data-test-link-to-location
                    {{on "click" (fn this.switchLocation location)}}
                    class="text-carbon-70 hover:grayDark no-underline
                      {{if location.disabledToEmployeesAt 'opacity-50'}}"
                    href="#"
                  >
                    {{location.nameWithCompanyName}}
                    {{#if location.disabledToEmployeesAt}}
                      <span data-test-location-disabled>(Disabled)</span>
                    {{/if}}
                  </a>
                </div>
              </td>
              {{#if (gt @groups.length 0)}}
                <td class="p-0 w-1/2 align-middle border-t">
                  <div data-test-group-selection class="pl-4 pt-4">
                    <LocationsTableGroupSelection
                      class="max-w-sm"
                      @location={{location}}
                      @groups={{this.sortedGroups}}
                    />
                  </div>
                </td>
              {{/if}}
            </tr>
          {{else}}
            <tr>
              <td colspan="100%" class="text-carbon-50 text-center pt-6">
                No results found
              </td>
            </tr>
          {{/each}}
        </tbody>
      </table>

      {{#if this.showBulkModalAssignment}}
        <LocationsTableBulkModalAssignment
          class="max-w-sm"
          @closeModal={{fn (mut this.showBulkModalAssignment) false}}
          @assignGroupTask={{this.assignGroupTask}}
          @groups={{this.sortedGroups}}
        />
      {{/if}}
    </div>
  </template>
}
